import React, { useEffect, useRef, useState } from "react";

// COMPONENT IMPORTS
import { ArrorwBottom } from "icons/sidebar/ArrorwBottom";
import { useOutsideClick } from "utils/HelperFunctions";

// PROPS TYPE
interface InputSelectProps {
  error?: string;
  label?: string;
  placeholder?: string;
  showLabel?: boolean;
  disabled?: boolean;
  height?: string;
  fontSize?: string;
  options?: any;
  selected?: any;
  setSelected?: any;
  isSearchFeature?: boolean;
  searchFieldType?: string;
  isUpDropDown?: boolean;
  className?: string;
  _className?: string;
  optionClassName?: string;
  arrowClassName?: string;
  isManual?: boolean;
  tabInd?: number;
  "data-testid"?: string;
  "aria-label"?: string;
}

/* ------------------------------------- SELECT ------------------------------------- */

const InputSelect = (props: InputSelectProps) => {
  const {
    error,
    label = "label",
    placeholder = "Placeholder",
    showLabel = true,
    disabled = false,
    fontSize,
    options = [],
    selected,
    setSelected,
    isSearchFeature = false,
    searchFieldType = "text",
    isUpDropDown = false,
    className = "",
    _className = "",
    optionClassName = "",
    arrowClassName = "",
    isManual = false,
    ...selectProps
  } = props;

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [dropUp, setDropUp] = useState(false);
  const [shouldRenderDropdown, setShouldRenderDropdown] = useState(false);
  const [query, setQuery] = useState<any>(selected ?? "");
  // The option the keyboard is on; it only becomes the selection on Enter
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const movedByArrowKey = useRef<boolean>(false);
  const dropDownRef = useRef<any>();
  const pressStartedHere = useRef<boolean>(false);
  const [filteredOptions, setFilteredOptions] = useState(options);
  // const [isUpDropDown, setIsUpDropDown] = useState(false); // Added state to control dropdown direction
  // Outside click listener
  useOutsideClick(dropDownRef, () => setIsOpen(false));

  useEffect(() => {
    if (isOpen) {
      setQuery("");
      setFilteredOptions(options);
      setActiveIndex(options?.findIndex(isSelectedOption) ?? -1);

      // Delay rendering until dropdown direction is calculated
      const rect = dropDownRef.current?.getBoundingClientRect();
      const dropdownHeight = 275;
      const windowHeight = window.innerHeight;
      const spaceBelow = windowHeight - rect.bottom;
      const spaceAbove = rect.top;

      if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
        setDropUp(true);
      } else {
        setDropUp(false);
      }

      // Wait one animation frame before rendering dropdown
      requestAnimationFrame(() => {
        setShouldRenderDropdown(true);
      });
    } else {
      setShouldRenderDropdown(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (query && typeof query === "string" && query?.trim() === "") {
      setFilteredOptions(options);
    }
  }, []);

  const normalise = (value: any) =>
    typeof value === "string" ? value.toLowerCase().replace(/\s+/g, "") : "";

  function isSelectedOption(option: any) {
    return isManual
      ? option?.name === selected?.name
      : option?.value === selected ||
          (typeof option?.value === "string" &&
            typeof selected === "string" &&
            option.value.toLowerCase() === selected.toLowerCase());
  }

  useEffect(() => {
    const needle = normalise(query);

    // An empty query means the user has not typed anything yet — opening the
    // dropdown resets the query, so clearing the selection here would wipe a
    // value the caller already had (e.g. a pre-filled billing country).
    if (!needle) {
      setFilteredOptions(options);
      return;
    }

    const matches = options.filter((option: any) =>
      normalise(option?.name).includes(needle)
    );
    // Rank names that start with what was typed first, so typing "u" lands on
    // "Uganda" rather than the first name that merely contains a "u".
    const prefixed = matches.filter((option: any) =>
      normalise(option?.name).startsWith(needle)
    );
    const ordered = [
      ...prefixed,
      ...matches.filter((option: any) => !prefixed.includes(option)),
    ];

    setFilteredOptions(ordered);
    setActiveIndex(ordered.length > 0 ? 0 : -1);
    setSelected(ordered.length > 0 ? ordered[0] : null);
  }, [query]);

  useEffect(() => {
    // Long lists scroll, so keep the keyboard's option in view. Only the arrow
    // keys move the view: opening a list must not shift the page under a mouse.
    if (!movedByArrowKey.current) {
      return;
    }
    movedByArrowKey.current = false;
    dropDownRef.current
      ?.querySelector('[data-active-option="true"]')
      ?.scrollIntoView?.({ block: "nearest" });
  }, [activeIndex]);

  // Calculate available space and set dropdown direction
  // useEffect(() => {
  //   const handleResize = () => {
  //     const rect = dropDownRef.current?.getBoundingClientRect();
  //     const spaceBelow = window.innerHeight - rect.bottom;
  //     const spaceAbove = rect.top;

  //     // Check if space below is enough for the dropdown, if not, set it to open upwards
  //     if (spaceBelow < 260) {
  //       setIsUpDropDown(true);
  //     } else {
  //       setIsUpDropDown(false);
  //     }
  //   };

  //   handleResize(); // Call on initial render
  //   window.addEventListener("resize", handleResize); // Listen for window resizing

  //   return () => window.removeEventListener("resize", handleResize); // Clean up the event listener
  // }, []);

  const getSelectedVal = () => {
    if (selected) {
      let value = options.filter(
        (val: any) =>
          val?.value === selected ||
          (typeof val?.value == "string" &&
            val?.value?.toLowerCase() === selected?.toLowerCase())
      );
      return value?.[0]?.name;
    }
    return null;
  };

  const handleKeyDown = (e: any) => {
    if (e.key === "CapsLock" || e.key === "Control" || e.key === "Shift") {
    } else if (e.key === "Tab") {
      // Tab carries on into the search box of a list that has one
      if (!isSearchFeature) {
        setIsOpen(false);
      }
    } else if (e.key === "Escape") {
      setIsOpen(false);
    } else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
      } else if (filteredOptions?.length > 0) {
        const lastIndex = filteredOptions.length - 1;
        movedByArrowKey.current = true;
        if (e.key === "ArrowDown") {
          setActiveIndex(activeIndex >= lastIndex ? 0 : activeIndex + 1);
        } else {
          setActiveIndex(activeIndex <= 0 ? lastIndex : activeIndex - 1);
        }
      }
    } else if (e.key === "Backspace") {
      setQuery((state: any) => {
        let str = state.substring(0, state.length - 1);
        return str;
      });
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
      } else {
        setIsOpen(false);
        if (filteredOptions?.[activeIndex]) {
          setSelected(filteredOptions[activeIndex]);
        }
      }
    } else if (
      (e.key >= "a" && e.key <= "z") ||
      (e.key >= "A" && e.key <= "Z")
    ) {
      setIsOpen(true);
      setQuery((state: any) => state + e.key);
    }
  };

  return (
    <>
      <div
        ref={dropDownRef}
        className="relative flex flex-col gap-[3px] items-start"
      >
        <div className="absolute bottom-[-24px] text-xs text-red-400">
          {error}
        </div>
        {showLabel && (
          <div
            className={`lg:text-base text-xs text-txtSecondary font-semibold leading-normal uppercase ${_className}`}
          >
            {label}
          </div>
        )}

        <div
          tabIndex={0}
          role="combobox"
          aria-expanded={isOpen}
          aria-label={selectProps["aria-label"] || label}
          data-testid={selectProps["data-testid"]}
          onPointerDown={() => {
            pressStartedHere.current = true;
          }}
          onClick={() => {
            // On iOS, dismissing the on-screen keyboard shifts the layout between
            // pointerdown and click, so a tap aimed at another element (e.g. a submit
            // button) can get its click retargeted here — ignore those stray clicks.
            if (!pressStartedHere.current) return;
            pressStartedHere.current = false;
            !disabled && setIsOpen(!isOpen);
          }}
          onKeyDown={handleKeyDown}
          className={`${
            disabled ? "!bg-bgThird !border-strokePrimary" : ""
          } text-txtPrimary relative w-full border-strokePrimary hover:!border-strokeActive  hover:!border- rounded-[4px] border lg:py-4 py-3 px-[15px] bg-bgPrimary lg:lg:text-base text-sm font-normal leading-normal focus:outline-none focus-visible:!border-strokeActive focus-visible:shadow-[0_0_0_1px_var(--color-blue)] ${
            isManual
              ? selected?.name
                ? "text-txtPrimary"
                : "text-txtSecondary"
              : getSelectedVal()
                ? "text-txtPrimary"
                : "text-txtSecondary"
          } ${className}`}
        >
          {isManual
            ? selected?.name
              ? selected?.name
              : placeholder
            : getSelectedVal()
              ? getSelectedVal()
              : placeholder}
          <ArrorwBottom
            className={`absolute right-[14px] lg:top-5 top-4 ${arrowClassName} ${
              !isOpen ? "rotate-[360deg]" : "rotate-180"
            }`}
          />
        </div>
        {/* OPTIONS */}
        {isOpen && shouldRenderDropdown ? (
          options?.length ? (
            <div
              className={`absolute w-full bg-white shadow-lg ${
                dropUp ? "bottom-[calc(100%-20px)]" : "top-[100%]"
              } max-h-[260px] overflow-y-auto rounded-[7px] z-10 border-strokePrimary hover:!border-strokeActive  border pt-2 pb-[10px] flex flex-col gap-[2px]`}
            >
              {isSearchFeature && (
                <div className="flex items-center overflow-hidden focus-visible:none bg-white focus-visible:outline-none h-[40px] w-full rounded-t-[7px] bg-moon-white px-[13px] py-[7px] text-sm border-strokePrimary hover:!border-strokeActive  border-b-[1px]">
                  {/* <img
                  className="w-[14px] h-[15px] cursor-pointer"
                  src={SearchIcon}
                  alt="SearchIcon"
                /> */}
                  <input
                    type={searchFieldType}
                    value={query}
                    placeholder="Search"
                    onChange={(e) => {
                      setQuery(e.target.value);
                    }}
                    style={{ fontSize: fontSize ? fontSize : "" }}
                    className="focus-visible:none bg-white focus-visible:outline-none h-full rounded-[7px] bg-moon-white pl-[6px] grow text-[15px]  placeholder-txtSecondary"
                  />
                </div>
              )}
              <div
                className={`${
                  isSearchFeature && "max-h-[220px] h-auto overflow-y-auto"
                }`}
              >
                {filteredOptions.length > 0 &&
                  filteredOptions.map((itm: any, index: number) => {
                    return (
                      <div
                        key={itm?.value ?? itm?.name ?? index}
                        style={{
                          fontSize: fontSize ? fontSize : "",
                        }}
                        role="option"
                        aria-selected={
                          isManual
                            ? selected?.value === itm?.value
                            : selected === itm?.value
                        }
                        data-testid={
                          selectProps["data-testid"]
                            ? `${selectProps["data-testid"]}-option-${String(
                                itm?.value ?? itm?.name ?? index
                              )
                                .trim()
                                .replace(/[^a-zA-Z0-9]+/g, "-")
                                .replace(/^-+|-+$/g, "")
                                .toLowerCase()}`
                            : undefined
                        }
                        data-active-option={index === activeIndex}
                        className={`flex justify-between px-[15px] text-sm py-[11px] hover:bg-bgSecondary cursor-pointer ${
                          index === activeIndex ? "bg-bgSecondary" : ""
                        }`}
                        onClick={() => {
                          setIsOpen(false);
                          setSelected(itm);
                        }}
                      >
                        <div className="flex items-center gap-[7px]">
                          {itm?.img && (
                            <img
                              src={itm?.img}
                              alt="img"
                              className="h-[16px] w-[16px]"
                            />
                          )}
                          {itm?.icon && (
                            <span className="h-[16px] w-[16px]">
                              {itm.icon}
                            </span>
                          )}
                          <div
                            className={`text-txtPrimary text-sm leading-5 font-normal ${optionClassName}`}
                          >
                            {itm?.name}
                          </div>
                        </div>
                        {/* {getSelectedVal() === itm?.name && (
                      <>
                        <img
                          className="w-[12px] h-[9px] flex self-center"
                          src={singleTick}
                          alt="Tick"
                        />
                      </>
                    )} */}
                      </div>
                    );
                  })}
                {filteredOptions.length === 0 && (
                  <div className="capitalize flex justify-center py-3 text-txtSecondary text-sm leading-5">
                    No options found
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div
              className={`absolute w-full bg-white shadow-lg ${
                isUpDropDown ? "bottom-[calc(100%-18px)]" : "top-[100%]"
              } max-h-[260px] overflow-y-auto rounded-[7px] z-10 border-strokePrimary hover:!border-strokeActive  border pt-2 pb-[10px] flex flex-col gap-[2px]`}
            >
              <div className="capitalize flex justify-center py-3 text-txtSecondary text-sm leading-5">
                No options found
              </div>
            </div>
          )
        ) : null}
      </div>
    </>
  );
};
export default InputSelect;
